<template>
    <div id="app-content" ref="appContent" class="close-navigation">
        <club-helper />
        <navigation-menu />
        <dev-extreme-locale />

        <div id="main-account">
            <account-header :title="pageTitle" />

            <div id="club-info-small-resolution" class="d-block d-lg-none">
                <selected-club-info />
            </div>

            <div class="page-title__small-resolution d-block d-lg-none">
                <span v-if="!shouldSeeAthleteDropdown" class="d-block font-size--sm font-weight-bold px-3 py-1">
                    {{ formattedTitle }}
                </span>
                <header-profile-coach v-if="shouldSeeAthleteDropdown" class="d-block" />
            </div>

            <router-view />
        </div>

        <slide-in-from-top>
            <session-import-data v-if="currentlyOpenModal === $options.SESSION_IMPORT_DATA" key="sessionImportData" />
            <import-file-german v-if="currentlyOpenModal === $options.IMPORT_FILE_GERMAN" key="importFileGerman" />
            <add-session-results v-if="currentlyOpenModal === $options.ADD_SESSION_RESULTS" key="addSessionResults" />
            <create-morning-monitoring
                v-if="currentlyOpenModal === $options.CREATE_MORNING_MONITORING"
                key="createMorningMonitoring"
            />
            <add-note v-if="currentlyOpenModal === $options.ADD_NOTE" key="addNote" />
            <join-club v-if="currentlyOpenModal === $options.JOIN_CLUB" key="joinClub" />
        </slide-in-from-top>

        <intercom-helper />
        <sentry-helper />
        <global-notifications />
        <polling-helper />
        <layout-helper />
        <socket-io />
    </div>
</template>

<script>
import { IS_PRODUCTION } from '@/constants/global';
import {
    ADD_NOTE,
    ADD_SESSION_RESULTS,
    CREATE_MORNING_MONITORING,
    IMPORT_FILE_GERMAN,
    JOIN_CLUB,
    SESSION_IMPORT_DATA
} from '@/constants/modals';
import {
    ROUTES_BILLING,
    ROUTES_BILLING_LICENCE_EXPIRED,
    ROUTES_CREATE_CLUB,
    ROUTES_PROFILE_DRV,
    ROUTES_PROFILE_MORNING_MONITORING,
    ROUTES_PROFILE_NOTES,
    ROUTES_PROFILE_OVERVIEW,
    ROUTES_PROFILE_REPORT_BIKE,
    ROUTES_PROFILE_REPORT_ERGO,
    ROUTES_PROFILE_REPORT_ROWING,
    ROUTES_PROFILE_REPORT_TRAINING_SUMMARY,
    ROUTES_PROFILE_SETTINGS,
    ROUTES_WELCOME
} from '@/constants/routes';
import { LUDUM_ERROR_CODES } from '@/constants';
const { NO_CLUBS, SUBSCRIPTION_EXPIRED } = LUDUM_ERROR_CODES;
import { LudumEventBus, TOGGLE_NAVIGATION } from '@/event-bus';
import { toUnixMillis } from '@/helpers/timeFormaterHelper';
import { mapGetters } from 'vuex';
import PollingHelper from '@/components/helpers/PollingHelper';
import SlideInFromTop from '@/components/transitions/SlideInFromTop';
import get from 'get-object-path';
import { Settings } from 'luxon';

export default {
    metaInfo() {
        const that = this;
        return {
            changed(info) {
                if (info.htmltitle) {
                    that.$store.dispatch('setPageTitle', info.htmltitle);
                } else {
                    that.$store.dispatch('setPageTitle', info.title);
                }
            }
        };
    },
    name: 'Home',

    ADD_NOTE,
    ADD_SESSION_RESULTS,
    CREATE_MORNING_MONITORING,
    IMPORT_FILE_GERMAN,
    JOIN_CLUB,
    SESSION_IMPORT_DATA,

    components: {
        AccountHeader: () => import(/*webpackChunkName: 'account-header'*/ '@/components/header/AccountHeader'),
        AddNote: () => import('@/components/add-note/AddNote'),
        AddSessionResults: () => import('@/components/add-session-results/AddSessionResults'),
        ClubHelper: () => import(/*webpackChunkName: 'club-helper'*/ '@/components/helpers/ClubHelper'),
        CreateMorningMonitoring: () => import('@/components/morning-monitoring/CreateMorningMonitoring'),
        DevExtremeLocale: () => import(/*webpackChunkName: 'dx-locale'*/ '@/components/helpers/DevExtremeLocale'),
        GlobalNotifications: () =>
            import(/*webpackChunkName: 'socket-io'*/ '@/components/global-notifications/GlobalNotifications'),
        HeaderProfileCoach: () => import('@/components/header/AccountHeaderProfileCoach'),
        ImportFileGerman: () => import('@/components/import-file-german/ImportFileGerman'),
        IntercomHelper: () => import(/*webpackChunkName: 'intercom'*/ '@/components/helpers/IntercomHelper'),
        JoinClub: () => import('@/components/settings/my-clubs/JoinClub.vue'),
        LayoutHelper: () => import(/*webpackChunkName: 'layout-helper'*/ '@/components/helpers/LayoutHelper'),
        NavigationMenu: () => import(/*webpackChunkName: 'navigation'*/ '@/components/navigation/NavigationMenu'),
        PollingHelper,
        SelectedClubInfo: () => import(/*webpackChunkName: 'navigation'*/ '@/components/navigation/SelectedClubInfo'),
        SentryHelper: () => import(/*webpackChunkName: 'sentry'*/ '@/components/helpers/SentryHelper'),
        SessionImportData: () => import('@/components/session/import-file-modal/SessionImportData'),
        SlideInFromTop,
        SocketIo: () => import(/*webpackChunkName: 'socket-io'*/ '@/components/helpers/SocketIo')
    },

    data() {
        return {
            metaData: {
                title: process.env.VUE_APP_LUDUM_TITLE
            }
        };
    },

    computed: {
        ...mapGetters({
            canViewAthleteProfile: 'userPermissions/canViewAthleteProfile',
            currentlyOpenModal: 'currentlyOpenModal',
            hasBillingPermissionInSelectedClub: 'hasBillingPermissionInSelectedClub',
            me: 'clubHelper/me',
            pageTitle: 'pageTitle',
            selectedClub: 'selectedClub',
            userAllRoles: 'userAllRoles'
        }),

        formattedTitle() {
            return this.pageTitle.split(`- ${process.env.VUE_APP_LUDUM_TITLE}`)[0];
        },

        shouldSeeAthleteDropdown() {
            return this.isOnProfilePage && this.canViewAthleteProfile && this.profileId !== this.me.id;
        },

        isOnProfilePage() {
            return [
                ROUTES_PROFILE_DRV,
                ROUTES_PROFILE_MORNING_MONITORING,
                ROUTES_PROFILE_NOTES,
                ROUTES_PROFILE_OVERVIEW,
                ROUTES_PROFILE_REPORT_BIKE,
                ROUTES_PROFILE_REPORT_ERGO,
                ROUTES_PROFILE_REPORT_ROWING,
                ROUTES_PROFILE_REPORT_TRAINING_SUMMARY,
                ROUTES_PROFILE_SETTINGS
            ].includes(this.$route.name);
        },

        profileId() {
            return this.$route.params.id ? Number(this.$route.params.id) : this.me.id;
        }
    },

    watch: {
        selectedClub: {
            immediate: true,
            handler(newClub, oldClub) {
                if (!newClub || (oldClub && newClub && newClub.club_id === oldClub.club_id)) return;
                this.$store.dispatch('updateErrorCode', null);
                this.$store.dispatch('checkToken').then(response => {
                    const errorCode = get(response, 'data.status_code');
                    this.$store.dispatch('updateErrorCode', errorCode);
                    const route = this.hasBillingPermissionInSelectedClub
                        ? ROUTES_BILLING
                        : ROUTES_BILLING_LICENCE_EXPIRED;

                    if (errorCode === SUBSCRIPTION_EXPIRED && this.$route.name !== route) {
                        this.$router.push({ name: route }).catch(() => {});
                    }

                    if (errorCode === SUBSCRIPTION_EXPIRED && this.$route.name === route) {
                        //location.reload();
                    }

                    // NO_CLUBS is now handled globally in the HTTP layer
                });
            }
        }
    },

    async created() {
        LudumEventBus.$on(TOGGLE_NAVIGATION, () => {
            this.$refs.appContent && this.$refs.appContent.classList.toggle('close-navigation');
        });

        this.initLocale();
        //this.saveHeapProperties();
        this.loadGoogleMaps();
        this.loadGoogleTagManager();
    },

    mounted() {
        this.$nextTick(() => {
            this.loadNavigationExpand();
        });
    },

    destroyed() {
        //this.resetHeap();
    },

    methods: {
        initLocale() {
            Settings.defaultLocale = (Vue.cookie && Vue.cookie.get('language')) || 'en';
        },

        loadGoogleTagManager() {
            this.$loadScript('https://www.googletagmanager.com/gtag/js')
                .then(() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    }
                    window.gtag = gtag;
                    gtag('js', new Date());
                    if (Vue.auth.isAuthenticated()) {
                        gtag('config', 'UA-58100688-4', {
                            user_id: this.me.id
                        });
                    } else {
                        gtag('config', 'UA-58100688-4');
                    }
                })
                .catch(() => {
                    // most probably add blocker is blocking access to gtag manager
                    window.gtag = () => {};
                });
        },

        loadGoogleMaps() {
            this.$store.commit('clubHelper/updateGoogleMaps', false);
            return this.$loadScript(
                `https://maps.googleapis.com/maps/api/js?v=3.47.6&key=${process.env.VUE_APP_GOOGLE_API_KEY}&libraries=places&language=${this.$i18n.locale}`
            )
                .then(() => {
                    this.$store.commit('clubHelper/updateGoogleMaps', true);
                })
                .catch(error => {
                    // Failed to fetch script
                    console.info('Failed to load Google Maps', error);
                    this.$store.commit('clubHelper/updateGoogleMaps', false);
                });
        },

        /*resetHeap() {
            IS_PRODUCTION && window.heap.resetIdentity();
        },

        saveHeapProperties() {
            if (IS_PRODUCTION) {
                window.heap.identify(this.me.id);
                window.heap.addUserProperties({
                    Name: this.me.full_name,
                    email: this.me.email,
                    created_at: toUnixMillis(this.me.created_at),
                    role: get(this.selectedClub, 'roles.data[0].name'),
                    all_roles: this.userAllRoles
                });
            }
        },*/

        loadNavigationExpand() {
            if (localStorage.getItem('ludum-navigation-expand') === null) {
                localStorage.setItem('ludum-navigation-expand', false);
            } else {
                const navigationExpand = localStorage.getItem('ludum-navigation-expand');

                const appContent = document.getElementById('app-content');
                if (JSON.parse(navigationExpand)) {
                    appContent && appContent.classList.remove('close-navigation');
                } else {
                    appContent && appContent.classList.add('close-navigation');
                }
            }
        }
    }
};
</script>
